import {
    ApiNetworkAnalyticsContextSchema,
    ApiNetworkContextTokenSchema,
    type ApiNetworkAnalyticsContext,
    type ApiNetworkContextBootstrapState,
} from "@bridge-applications/pb-types";
import { z } from "zod";

const NETWORK_CONTEXT_CACHE_KEY = "pbtg:network-analytics-context:v1";
export const NETWORK_CONTEXT_TOKEN_HEADER =
    "X-PB-Network-Context-Token";

const NetworkContextCacheSchema = z
    .object({
        schemaVersion: z.literal(1),
        userId: z.uuid(),
        telegramUserId: z.number().int().positive(),
        token: ApiNetworkContextTokenSchema,
        cacheState: z.enum([
            "CACHED",
            "CURRENT",
            "STALE",
            "UNAVAILABLE",
        ]),
        context: ApiNetworkAnalyticsContextSchema,
    })
    .strict();

type NetworkContextCache = z.infer<typeof NetworkContextCacheSchema>;

export interface NetworkContextStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

function readCache(
    storage: NetworkContextStorage | null,
    telegramUserId: number | null | undefined,
): NetworkContextCache | null {
    if (!storage || telegramUserId == null) return null;
    try {
        const raw = storage.getItem(NETWORK_CONTEXT_CACHE_KEY);
        if (!raw) return null;
        const parsed: unknown = JSON.parse(raw);
        const cache = NetworkContextCacheSchema.parse(parsed);
        return cache.telegramUserId === telegramUserId ? cache : null;
    } catch {
        try {
            storage.removeItem(NETWORK_CONTEXT_CACHE_KEY);
        } catch {
            // Storage is only an analytics optimization.
        }
        return null;
    }
}

function writeCache(
    storage: NetworkContextStorage | null,
    cache: NetworkContextCache,
): void {
    if (!storage) return;
    try {
        storage.setItem(
            NETWORK_CONTEXT_CACHE_KEY,
            JSON.stringify(NetworkContextCacheSchema.parse(cache)),
        );
    } catch {
        // Storage is only an analytics optimization.
    }
}

function effectiveCacheState(
    cache: NetworkContextCache,
    nowMilliseconds: number,
): NetworkContextCache["cacheState"] {
    return Date.parse(cache.context.expiresAt) <= nowMilliseconds
        ? "STALE"
        : cache.cacheState;
}

function anonymityClass(
    context: ApiNetworkAnalyticsContext,
):
    | "TOR"
    | "RESIDENTIAL_PROXY"
    | "VPN"
    | "PROXY"
    | "RELAY"
    | "HOSTING"
    | "ANONYMOUS"
    | "DIRECT" {
    if (context.isTor) return "TOR";
    if (context.isResidentialProxy) return "RESIDENTIAL_PROXY";
    if (context.isVpn) return "VPN";
    if (context.isProxy) return "PROXY";
    if (context.isRelay) return "RELAY";
    if (context.isHosting) return "HOSTING";
    if (context.isAnonymous) return "ANONYMOUS";
    return "DIRECT";
}

export function browserNetworkContextStorage(): NetworkContextStorage | null {
    if (typeof window === "undefined") return null;
    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

/**
 * Marks a previous-session value as not yet checked against this launch's IP.
 * This runs before PostHog initializes, so the automatic initial pageview gets
 * explicit freshness metadata without being delayed.
 */
export function beginNetworkAnalyticsSession(
    storage: NetworkContextStorage | null,
    telegramUserId: number | null | undefined,
    nowMilliseconds = Date.now(),
): void {
    const cache = readCache(storage, telegramUserId);
    if (!cache) return;
    writeCache(storage, {
        ...cache,
        cacheState:
            Date.parse(cache.context.expiresAt) <= nowMilliseconds
                ? "STALE"
                : "CACHED",
    });
}

export function networkContextRequestHeaders(
    storage: NetworkContextStorage | null,
    telegramUserId: number | null | undefined,
    nowMilliseconds = Date.now(),
): Record<string, string> {
    const cache = readCache(storage, telegramUserId);
    if (
        !cache ||
        Date.parse(cache.context.expiresAt) <= nowMilliseconds
    ) {
        return {};
    }
    return { [NETWORK_CONTEXT_TOKEN_HEADER]: cache.token };
}

export function applyNetworkContextBootstrapState(
    storage: NetworkContextStorage | null,
    telegramUserId: number | null | undefined,
    state: ApiNetworkContextBootstrapState | undefined,
): void {
    if (!state) return;
    const cache = readCache(storage, telegramUserId);
    if (!cache) return;
    writeCache(storage, {
        ...cache,
        cacheState:
            state === "UNCHANGED"
                ? "CURRENT"
                : state === "REFRESHING"
                  ? "STALE"
                  : "UNAVAILABLE",
    });
}

export function storeResolvedNetworkContext(
    storage: NetworkContextStorage | null,
    input: {
        userId: string;
        telegramUserId: number;
        token: string;
        context: ApiNetworkAnalyticsContext;
    },
): void {
    writeCache(storage, {
        schemaVersion: 1,
        userId: input.userId,
        telegramUserId: input.telegramUserId,
        token: input.token,
        cacheState: "CURRENT",
        context: input.context,
    });
}

export function networkAnalyticsEventProperties(
    storage: NetworkContextStorage | null,
    telegramUserId: number | null | undefined,
    nowMilliseconds = Date.now(),
): Record<string, unknown> {
    const cache = readCache(storage, telegramUserId);
    if (!cache) {
        return {
            network_context_status: "unknown",
            network_intel_schema_version: 1,
        };
    }

    const context = cache.context;
    const cacheState = effectiveCacheState(cache, nowMilliseconds);
    const classifiedAt = Date.parse(context.classifiedAt);
    const ageHours = Number.isFinite(classifiedAt)
        ? Math.max(
              0,
              Math.floor(
                  (nowMilliseconds - classifiedAt) / (60 * 60 * 1_000),
              ),
          )
        : 0;

    return {
        network_context_status:
            cacheState === "CACHED"
                ? "cached_unverified"
                : cacheState.toLowerCase(),
        network_context_age_hours: ageHours,
        network_anonymity_class: anonymityClass(context),
        network_is_anonymous: context.isAnonymous,
        network_is_vpn: context.isVpn,
        network_is_proxy: context.isProxy,
        network_is_tor: context.isTor,
        network_is_relay: context.isRelay,
        network_is_residential_proxy: context.isResidentialProxy,
        network_is_hosting: context.isHosting,
        network_as_type: context.asType,
        network_country_code: context.countryCode,
        network_intel_provider: context.provider,
        network_intel_schema_version: context.schemaVersion,
    };
}
